<template>
  <div>
    <div class="intro-section__headline">
      <h4 class="intro-section__headline__txt">
     <span>
          {{ $t('pages.signup.headline_txt') }}
          <router-link :to="{ name: 'login' }" exact class="text-primary">
            {{ $t('forms.signup_form.btn_login') }}
          </router-link>
        </span>
      </h4>
    </div>
    <form @submit.prevent="handleSignup" class="intro-section__form">
      <div class="form-group" :class="{'form-group--error': hasValidationError('email')}">
        <input
            type="email"
            v-model="email"
            required
            class="input input-lg form-control"
            :placeholder="$t('forms.signup_form.email')"
            :disabled="loading"
        />
        <span class="form-group--error__message" v-if="hasValidationError('email')">
          {{ validationErrors.email[0] }}
        </span>
      </div>

      <div class="form-group" :class="{'form-group--error': hasValidationError('password')}">
        <input-password
            v-model="password"
            :required="true"
            :autocomplete="'new-password'"
            :placeholder="$t('forms.signup_form.password')"
            :disabled="loading"
        />
        <span class="form-group--error__message" v-if="hasValidationError('password')">
          {{ validationErrors.password }}
        </span>
      </div>

      <transition name="slide">
        <div class="form-group" v-if="password">
          <input-password
              v-model="passwordConfirm"
              :required="true"
              :autocomplete="'new-password'"
              :extra-classes="'input-confirm'"
              :placeholder="$t('forms.signup_form.password_confirm')"
              :paste="() => false"
              :disabled="loading"
          />
        </div>
      </transition>

      <div class="form-check-container">
        <div class="form-check" v-if="appConfigReady">
          <div v-if="privacyCheckboxes && privacyCheckboxes.length">
            <template v-for="checkbox in privacyCheckboxes">
              <b-form-checkbox
                  v-model="userCheckboxChoices[checkbox.field_name]"
                  :required="checkbox.is_required"
                  :name="checkbox.field_name"
                  :value="true"
                  :key="checkbox.field_name"
                  :unchecked-value="false"
                  :disabled="loading"
              >
                <span v-html="checkbox.label"></span>
              </b-form-checkbox>
            </template>
          </div>
          <div v-else>
            <b-form-checkbox
                v-model="consent_privacy_and_terms"
                required
                name="consent_privacy_and_terms"
                value="true"
                unchecked-value="false"
                :disabled="loading"
            >
              <span v-html="privacyStatement"></span>
            </b-form-checkbox>
          </div>

          <span class="form-group--error__message" v-if="hasValidationError('consent_privacy_and_terms')">
            {{ validationErrors.consent_privacy_and_terms }}
          </span>
        </div>
      </div>

      <div class="form-group">
        <button type="submit" class="btn btn-primary m-0 w-100" :disabled="loading">
          <b-spinner small label="Loading..." v-if="loading"></b-spinner>
          <span v-else>{{ $t('forms.signup_form.create_account_btn') }}</span>
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import InputPassword from '@/components/InputPassword'
import AuthService from '@/domain/auth/service'
import {policyUrl} from '@/utils'

export default {
  name: 'PasswordSignupForm',

  components: {
    InputPassword
  },

  data() {
    return {
      email: '',
      password: '',
      passwordConfirm: '',
      consent_privacy_and_terms: false,
      loading: false,
      validationErrors: {},
      userCheckboxChoices: {}
    }
  },

  computed: {
    ...mapGetters(['thirdLevelDomain']),
    ...mapGetters('appConfig', ['getPrivacyCheckboxes', 'getAppConfigReady', 'getEventOwnerId']),

    appConfigReady() {
      return this.getAppConfigReady
    },

    privacyCheckboxes() {
      return this.getPrivacyCheckboxes
    },

    useStrongPassword() {
      return this.thirdLevelDomain !== undefined && this.thirdLevelDomain.includes('watchesandwonders')
    },

    privacyStatement() {
      return this.$t('forms.signup_form.privacy_statement', {
        terms_url: policyUrl().terms,
        privacy_url: policyUrl().privacy
      })
    }
  },

  watch: {
    appConfigReady: {
      handler() {
        this.setUserCheckboxChoices()
      },
      immediate: true
    }
  },

  methods: {
    ...mapActions('auth', ['authenticate']),

    setUserCheckboxChoices() {
      let defaultModel = {}
      const checkboxes = this.privacyCheckboxes
      checkboxes.forEach(function (checkbox) {
        defaultModel[checkbox.field_name] = false
      })
      this.$set(this, 'userCheckboxChoices', defaultModel)
    },

    validateFormData() {
      this.validationErrors = {}

      if (this.password !== this.passwordConfirm) {
        this.$set(this.validationErrors, 'password', this.$t('forms.signup_form.password_error_match'))
        return false
      }

      if (this.useStrongPassword) {
        const passwordRegex = /^(?=.*\d)(?=.*[A-Z])(?=.*\W).{8,}$/
        if (!passwordRegex.test(this.password)) {
          this.$set(this.validationErrors, 'password', this.$t('forms.signup_form.password_error_invalid'))
          return false
        }
      }

      return true
    },

    async handleSignup() {
      if (!this.validateFormData()) {
        return false
      }

      this.loading = true
      this.validationErrors = {}

      let payload = {
        email: this.email,
        password: this.password,
        language: this.$i18n.locale
      }

      if (this.getEventOwnerId && this.privacyCheckboxes.length) {
        payload.consent_third_party_fields = this.userCheckboxChoices
      } else {
        payload.consent_privacy_and_terms = this.consent_privacy_and_terms
      }

      try {
        await AuthService.createAccount(payload)

        // Auto-login after signup
        await this.authenticate({
          method: 'password',
          credentials: {
            email: this.email,
            password: this.password
          },
          remember: true
        })

        this.$emit('success')
      } catch (error) {
        console.error('Signup error:', error)
        this.validationErrors = Object.assign({}, this.validationErrors, error.response.data)
      } finally {
        this.loading = false
      }
    },

    hasValidationError(fieldName) {
      return this.validationErrors.hasOwnProperty(fieldName)
    }
  }
}
</script>

<style scoped>

</style>
